import { Component, DestroyRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, inject } from '@angular/core'
import { AsyncPipe } from '@angular/common'
import { HttpErrorResponse } from '@angular/common/http'
import { A11yModule } from '@angular/cdk/a11y'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { BehaviorSubject, Subject, switchMap, catchError, EMPTY } from 'rxjs'
import { TransactionDetails, ExportHistoryEntry, DataSourcesBlock } from '../../models'
import { TransactionDetailsApiService } from '../../services/transaction-details-api.service'
import { TransactionOverviewComponent } from '../transaction-overview/transaction-overview.component'
import { AuthorizationDetailsComponent } from '../authorization-details/authorization-details.component'
import { NotificationTableComponent } from '../notification-table/notification-table.component'
import { TransactionHistoryComponent } from '../transaction-history/transaction-history.component'
import { RelatedTransactionsComponent } from '../related-transactions/related-transactions.component'
import { ExportHistoryComponent } from '../export-history/export-history.component'
import { NocInfoComponent } from '../noc-info/noc-info.component'

@Component({
    selector: 'ach-transaction-details-modal',
    standalone: true,
    imports: [
        AsyncPipe,
        A11yModule,
        TransactionOverviewComponent,
        AuthorizationDetailsComponent,
        NotificationTableComponent,
        TransactionHistoryComponent,
        RelatedTransactionsComponent,
        ExportHistoryComponent,
        NocInfoComponent,
    ],
    templateUrl: './transaction-details-modal.component.html',
    styleUrl: './transaction-details-modal.component.scss',
})
export class TransactionDetailsModalComponent implements OnInit, OnChanges, OnDestroy {
    @Input({ required: true }) transactionId!: string
    @Input({ required: true }) hidden!: BehaviorSubject<boolean>
    @Input({ required: true }) sessionId!: string
    @Input() canExport = true
    @Output() transactionSelected = new EventEmitter<string>()

    details: TransactionDetails | null = null
    dataSources: DataSourcesBlock | null = null
    loading = false
    error: string | null = null
    canRetry = false

    readonly skeletonBlocks = [
        [1, 2, 3, 4, 5, 6],
        [1, 2],
        [1, 2, 3],
    ]

    private readonly api = inject(TransactionDetailsApiService)
    private readonly destroyRef = inject(DestroyRef)
    private readonly loadSubject = new Subject<string>()
    private currentLoadId: string | null = null
    private savedBodyOverflow = ''

    constructor() {
        this.loadSubject
            .pipe(
                switchMap((id) => {
                    this.loading = true
                    this.error = null
                    this.canRetry = false
                    this.currentLoadId = id
                    return this.api.getTransactionDetails(id, this.sessionId).pipe(
                        catchError((err: HttpErrorResponse) => {
                            this.error = this.getErrorMessage(err.status)
                            this.canRetry = this.isRetryableStatus(err.status)
                            this.loading = false
                            return EMPTY
                        }),
                    )
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((response) => {
                this.details = response.data
                this.dataSources = response.dataSources
                this.loading = false
            })
    }

    ngOnInit(): void {
        this.hidden
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((isHidden) => {
                if (!isHidden) {
                    this.savedBodyOverflow = document.body.style.overflow
                    document.body.style.overflow = 'hidden'
                    if (this.transactionId && this.transactionId !== this.currentLoadId) {
                        this.loadSubject.next(this.transactionId)
                    }
                } else {
                    document.body.style.overflow = this.savedBodyOverflow
                }
            })
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['transactionId'] && this.transactionId && this.transactionId !== this.currentLoadId) {
            this.loadSubject.next(this.transactionId)
        }
    }

    ngOnDestroy(): void {
        document.body.style.overflow = this.savedBodyOverflow
    }

    onTransactionSelected(id: string): void {
        this.transactionSelected.emit(id)
        this.loadSubject.next(id)
    }

    retry(): void {
        if (!this.currentLoadId) return
        this.canRetry = false
        this.loadSubject.next(this.currentLoadId)
    }

    isSourceUnavailable(source: keyof DataSourcesBlock): boolean {
        return this.dataSources?.[source]?.status === 'unavailable'
    }

    close(): void {
        this.details = null
        this.dataSources = null
        this.error = null
        this.canRetry = false
        this.loading = false
        this.currentLoadId = null
        this.hidden.next(true)
    }

    @HostListener('document:keydown.escape')
    onEscapeKey(): void {
        this.close()
    }

    downloading = false

    onDownload(): void {
        if (!this.details || this.downloading || !this.currentLoadId) return
        this.downloading = true
        const id = this.currentLoadId

        this.api.logExport(id, this.sessionId).pipe(
            switchMap(() => this.api.downloadPdf(id, this.sessionId)),
            catchError(() => {
                this.downloading = false
                return EMPTY
            }),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe((blob) => {
            const newEntry: ExportHistoryEntry = {
                userName: 'Current User',
                timeStamp: new Date().toISOString(),
            }
            this.details!.exportHistory = [newEntry, ...this.details!.exportHistory]
            this.downloading = false
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `ach-transaction-${id}.pdf`
            a.click()
            URL.revokeObjectURL(url)
        })
    }

    private isRetryableStatus(status: number): boolean {
        return status === 0 || status >= 500
    }

    private getErrorMessage(status: number): string {
        switch (status) {
            case 404:
                return 'Transaction not found.'
            case 401:
                return 'Session expired. Please close and reopen.'
            default:
                return 'Unable to load transaction details. Please try again.'
        }
    }
}
